import React from "react";
import SectionShell from "../../shared/SectionShell";

const documentCardStyle = {
  border: "1px solid var(--border, #e5e5e5)",
  borderRadius: 12,
  padding: 12,
  background: "#fafafa",
  display: "grid",
  gap: 8,
};

const StudentDocumentsTab = ({
  formData,
  handleChange,
  cardStyle,
  labelStyle,
  textareaStyle,
  dateInputStyle,
  miniActionBtnStyle,
  getDocBadgeStyle,
  REQUIRED_COMPLIANCE_DOCUMENTS,
  updateRequiredComplianceDocumentField,
  uploadRequiredComplianceDocument,
  removeRequiredComplianceDocument,
  handleDocumentUpload,
  removeComplianceDocument,
  openDocument,
  downloadDocument,
  printDocument,
}) => {
  return (
    <SectionShell
      title="Documents & Compliance"
      subtitle="Enrollment paperwork, compliance documents, and other records."
    >
      <div style={{ display: "grid", gap: 14 }}>
        <div style={cardStyle}>
          <div style={labelStyle}>Required Compliance Documents</div>

          <div style={{ display: "grid", gap: 12, marginTop: 8 }}>
            {REQUIRED_COMPLIANCE_DOCUMENTS.map((requiredDoc) => {
              const doc = formData.complianceRequiredDocuments?.[requiredDoc.key];

              return (
                <div key={requiredDoc.key} style={documentCardStyle}>
                  <div style={{ fontWeight: 800 }}>{requiredDoc.title}</div>

                  <div style={getDocBadgeStyle(doc?.status || "Missing")}>
                    {doc?.status || "Missing"}
                  </div>

                  <div style={{ color: "var(--text-secondary, #666)", fontSize: ".92rem" }}>
                    File: {doc?.title || doc?.name || "No file uploaded"}
                  </div>

                  <div style={{ display: "grid", gap: 6 }}>
                    <div style={labelStyle}>Due Date</div>

                    <input
                      type="date"
                      style={dateInputStyle}
                      value={doc?.due || ""}
                      onChange={(e) =>
                        updateRequiredComplianceDocumentField(
                          requiredDoc.key,
                          "due",
                          e.target.value
                        )
                      }
                    />
                  </div>

                  <div style={{ color: "var(--text-secondary, #666)" }}>
                    Uploaded:{" "}
                    {doc?.uploadedAt
                      ? new Date(doc.uploadedAt).toLocaleDateString()
                      : "Not uploaded"}
                  </div>

                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 14px",
                      borderRadius: 12,
                      border: "1px dashed var(--border, #ccc)",
                      background: "#fff",
                      cursor: "pointer",
                      fontWeight: 700,
                      width: "fit-content",
                    }}
                  >
                    Upload {requiredDoc.title}

                    <input
                      type="file"
                      hidden
                      onChange={(e) =>
                        uploadRequiredComplianceDocument(
                          requiredDoc.key,
                          e.target.files?.[0]
                        )
                      }
                    />
                  </label>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      style={miniActionBtnStyle}
                      onClick={() => openDocument(doc?.fileUrl)}
                    >
                      View
                    </button>

                    <button
                      type="button"
                      style={miniActionBtnStyle}
                      onClick={() =>
                        downloadDocument(doc?.fileUrl, doc?.title || requiredDoc.title)
                      }
                    >
                      Download
                    </button>

                    <button
                      type="button"
                      style={miniActionBtnStyle}
                      onClick={() => printDocument(doc?.fileUrl)}
                    >
                      Print
                    </button>

                    <button
                      type="button"
                      style={miniActionBtnStyle}
                      onClick={() =>
                        removeRequiredComplianceDocument(requiredDoc.key)
                      }
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={labelStyle}>Other Compliance Documents</div>

          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 14px",
              borderRadius: 12,
              border: "1px dashed var(--border, #ccc)",
              background: "#fff",
              cursor: "pointer",
              fontWeight: 700,
              width: "fit-content",
              marginTop: 8,
            }}
          >
            Upload Other Document

            <input
              type="file"
              multiple
              hidden
              onChange={(e) => handleDocumentUpload(e.target.files)}
            />
          </label>

          {(formData.documents || []).length ? (
            <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
              {(formData.documents || []).map((doc, idx) => (
                <div key={doc._id || `${doc.title}-${idx}`} style={documentCardStyle}>
                  <div style={{ fontWeight: 800 }}>
                    {doc.title || doc.name || "Untitled Document"}
                  </div>

                  <div style={getDocBadgeStyle(doc.status || "Missing")}>
                    {doc.status || "Missing"}
                  </div>

                  <div style={{ color: "var(--text-secondary, #666)" }}>
                    Uploaded:{" "}
                    {doc.uploadedAt
                      ? new Date(doc.uploadedAt).toLocaleDateString()
                      : "Not uploaded"}
                  </div>

                  <div style={{ color: "var(--text-secondary, #666)", fontSize: ".92rem" }}>
                    File: {doc?.title || doc?.name || "No file uploaded"}
                  </div>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      style={miniActionBtnStyle}
                      onClick={() => openDocument(doc.fileUrl)}
                    >
                      View
                    </button>

                    <button
                      type="button"
                      style={miniActionBtnStyle}
                      onClick={() =>
                        downloadDocument(doc.fileUrl, doc.title || doc.name)
                      }
                    >
                      Download
                    </button>

                    <button
                      type="button"
                      style={miniActionBtnStyle}
                      onClick={() => printDocument(doc.fileUrl)}
                    >
                      Print
                    </button>

                    <button
                      type="button"
                      style={miniActionBtnStyle}
                      onClick={() => removeComplianceDocument(doc._id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: "var(--text-secondary, #666)", marginTop: 12 }}>
              No other compliance documents uploaded yet.
            </div>
          )}
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={labelStyle}>Documents / Compliance Notes</div>

          <textarea
            style={textareaStyle}
            value={formData.documentsNote || ""}
            placeholder="Add notes about enrollment paperwork, compliance reminders, or missing documents..."
            onChange={(e) => handleChange("documentsNote", e.target.value)}
          />
        </div>
      </div>
    </SectionShell>
  );
};

export default StudentDocumentsTab;